import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Segmented } from "antd";
import type { TableColumnsType } from "antd";
import { saleStatsApi } from "api/saleStats.api";
import { AppButton, AppSelect, AppTable } from "components/antd-ui";
import { getTableShowingRange, StatusCell, TableContent, TableLayout, TableLayoutFooter } from "components/table";
import { fetchStaffOptions, useRemoteOptions } from "hooks/useRemoteOptions";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { userStore } from "store/userStore";
import { SaleStatsCounts, SaleStatsPeriod, SaleStatsRow, SaleStatsSection, SaleStatsSummary } from "types/saleStats";
import { todoStatusTrans } from "types/todo";
import { getTitle } from "utils";
import { can } from "utils/auth";
import dayjs, { Dayjs } from "utils/dayjs";
import { feedbackStatusTrans } from "views/Feedback/feedback.meta";
import { formatMoney, opportunityStatusTrans, quoteStatusTrans } from "views/Opportunity/opportunity.meta";
import { ORDER_PATH, orderStatusTrans } from "views/Order/order.meta";
import { customerApi } from "api/customer.api";
import { Customer } from "types/customer";
import { CustomerModal } from "views/Customer/components/CustomerModal";
import { OrderDetailModal } from "views/Order/OrderDetailModal";
import { OpportunityDetailModal } from "views/Opportunity/components/OpportunityDetailModal";
import { OPPORTUNITY_PATH, QUOTE_PATH } from "views/Opportunity/opportunity.meta";
import "./SaleStats.scss";

const salePath = "/sale-stats";

// Đầu kỳ: thứ Hai của tuần hoặc ngày 1 của tháng
const startOfPeriod = (date: Dayjs, period: SaleStatsPeriod) => (period === "month" ? date.startOf("month") : date.startOf("isoWeek"));
const dateTime = (value?: number) => (value ? dayjs.unix(value).format("DD/MM/YYYY HH:mm") : "--");
const dateOnly = (value?: number) => (value ? dayjs.unix(value).format("DD/MM/YYYY") : "--");
const text = (value?: string | null) => <TableContent>{value || "--"}</TableContent>;
const customerCell = (row: SaleStatsRow) => text(row.customer?.name);

const SECTIONS: { key: SaleStatsSection; label: string }[] = [
  { key: "customers", label: "Khách hàng mới" },
  { key: "opportunities", label: "Cơ hội mới" },
  { key: "quotes", label: "Báo giá mới" },
  { key: "orders", label: "Đơn hàng mới" },
  { key: "debts", label: "Thu hồi công nợ" },
  { key: "todos", label: "Công việc" },
  { key: "feedbacks", label: "Phản hồi" },
  { key: "schedules", label: "Lịch công tác" },
];

const countOf = (counts: SaleStatsCounts | undefined, key: SaleStatsSection) => (counts ? counts[key] : 0);

// Mục nào bấm vào dòng được để mở popup chi tiết, kèm quyền Xem cần có
const ROW_PERMISSION: Partial<Record<SaleStatsSection, string>> = {
  customers: "/customer",
  opportunities: OPPORTUNITY_PATH,
  quotes: QUOTE_PATH,
  orders: ORDER_PATH,
};

// Cột bảng của từng mục
const COLUMNS: Record<SaleStatsSection, TableColumnsType<SaleStatsRow>> = {
  customers: [
    { title: "Mã", dataIndex: "code", key: "code", width: 130, render: (v) => text(v) },
    { title: "Tên khách hàng", dataIndex: "name", key: "name", render: (v) => text(v) },
    { title: "Địa chỉ", dataIndex: "address", key: "address", render: (v) => text(v) },
    { title: "Ngày tạo", dataIndex: "createdAt", key: "createdAt", width: 150, render: (v) => dateTime(v) },
  ],
  opportunities: [
    { title: "Mã báo giá", dataIndex: "code", key: "code", width: 150, render: (v) => text(v) },
    { title: "Tên cơ hội", dataIndex: "name", key: "name", render: (v) => text(v) },
    { title: "Khách hàng", key: "customer", render: (_, row) => customerCell(row) },
    {
      title: "Trạng thái",
      key: "status",
      width: 170,
      render: (_, row) =>
        row.quoteStatus ? (
          <StatusCell value={row.quoteStatus as never} trans={quoteStatusTrans as never} />
        ) : (
          <StatusCell value={row.status as never} trans={opportunityStatusTrans as never} />
        ),
    },
    { title: "Ngày tạo", dataIndex: "createdAt", key: "createdAt", width: 150, render: (v) => dateTime(v) },
  ],
  quotes: [
    { title: "Mã báo giá", dataIndex: "code", key: "code", width: 150, render: (v) => text(v) },
    { title: "Tên cơ hội", dataIndex: "name", key: "name", render: (v) => text(v) },
    { title: "Khách hàng", key: "customer", render: (_, row) => customerCell(row) },
    {
      title: "Trạng thái",
      key: "quoteStatus",
      width: 170,
      render: (_, row) => <StatusCell value={row.quoteStatus as never} trans={quoteStatusTrans as never} />,
    },
    { title: "Ngày duyệt", dataIndex: "approvedAt", key: "approvedAt", width: 150, render: (v) => dateTime(v) },
  ],
  orders: [
    { title: "Mã đơn", dataIndex: "code", key: "code", width: 110, render: (v) => text(v) },
    { title: "Mã hợp đồng", dataIndex: "contractCode", key: "contractCode", width: 150, render: (v) => text(v) },
    { title: "Mã báo giá", dataIndex: "opportunityCode", key: "opportunityCode", width: 150, render: (v) => text(v) },
    { title: "Khách hàng", key: "customer", render: (_, row) => customerCell(row) },
    {
      title: "Trạng thái",
      key: "status",
      width: 150,
      render: (_, row) => <StatusCell value={row.status as never} trans={orderStatusTrans as never} />,
    },
    {
      title: "Tổng tiền (gồm VAT)",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 170,
      align: "right",
      render: (v) => <span className="font-semibold">{formatMoney(v || 0)}đ</span>,
    },
    { title: "Ngày tạo", dataIndex: "createdAt", key: "createdAt", width: 150, render: (v) => dateTime(v) },
  ],
  debts: [
    { title: "Khách hàng", key: "customer", render: (_, row) => customerCell(row) },
    { title: "Mã đơn", key: "order", width: 110, render: (_, row) => text(row.order?.code) },
    { title: "Mã hợp đồng", key: "contractCode", width: 150, render: (_, row) => text(row.order?.contractCode) },
    { title: "Đợt thu", dataIndex: "content", key: "content", render: (v) => text(v) },
    {
      title: "Ngày thu",
      dataIndex: "collectedDate",
      key: "collectedDate",
      width: 130,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "--"),
    },
    {
      title: "Số tiền (gồm VAT)",
      dataIndex: "amount",
      key: "amount",
      width: 170,
      align: "right",
      render: (v) => <span className="font-semibold">{formatMoney(v || 0)}đ</span>,
    },
  ],
  todos: [
    { title: "Công việc", dataIndex: "name", key: "name", render: (v) => text(v) },
    { title: "Khách hàng", key: "customer", render: (_, row) => customerCell(row) },
    { title: "Hạn", dataIndex: "deadlineAt", key: "deadlineAt", width: 130, render: (v) => dateOnly(v) },
    {
      title: "Trạng thái",
      key: "status",
      width: 140,
      render: (_, row) => <StatusCell value={row.status as never} trans={todoStatusTrans as never} />,
    },
  ],
  feedbacks: [
    { title: "Khách hàng", key: "customer", width: 240, render: (_, row) => customerCell(row) },
    { title: "Nội dung", dataIndex: "content", key: "content", render: (v) => text(v) },
    { title: "Thời gian", dataIndex: "feedbackAt", key: "feedbackAt", width: 150, render: (v) => dateTime(v) },
    {
      title: "Trạng thái",
      key: "status",
      width: 140,
      render: (_, row) => <StatusCell value={row.status as never} trans={feedbackStatusTrans as never} />,
    },
    { title: "Người xử lý", key: "handledStaff", width: 160, render: (_, row) => text(row.handledStaff?.name) },
  ],
  schedules: [
    { title: "Tiêu đề", dataIndex: "title", key: "title", render: (v) => text(v) },
    { title: "Khách hàng", key: "customer", render: (_, row) => customerCell(row) },
    { title: "Địa chỉ", dataIndex: "address", key: "address", render: (v) => text(v) },
    {
      title: "Thời gian",
      key: "time",
      width: 270,
      render: (_, row) => (
        <TableContent>
          {dateTime(row.startAt)} - {dateTime(row.endAt)}
        </TableContent>
      ),
    },
    {
      title: "Kết quả",
      key: "hasResult",
      width: 110,
      render: (_, row) => (row.hasResult ? <span className="text-green-600">Đã có</span> : <span className="text-gray-400">Chưa có</span>),
    },
  ],
};

export const SaleStatsPage = observer(({ title = "" }) => {
  const onlyMineRole = !userStore.info.role?.isAdmin && can(salePath, "ONLY_MINE");
  const myId = userStore.info.id;

  const [period, setPeriod] = useState<SaleStatsPeriod>("week");
  // Ngày mốc: giữ nguyên khi đổi Tuần/Tháng để quay lại đúng kỳ đang xem; đầu kỳ suy ra từ ngày mốc
  const [anchor, setAnchor] = useState<Dayjs>(dayjs());
  const periodStart = startOfPeriod(anchor, period);
  const [staffId, setStaffId] = useState<number | undefined>(myId);
  const [staffLabel, setStaffLabel] = useState<string>(userStore.info.name || userStore.info.username || "");
  const [summary, setSummary] = useState<SaleStatsSummary>();
  const [section, setSection] = useState<SaleStatsSection>("customers");
  const [rows, setRows] = useState<SaleStatsRow[]>([]);
  const [total, setTotal] = useState(0);
  const [paging, setPaging] = useState({ page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [orderId, setOrderId] = useState(0);
  const [opportunityId, setOpportunityId] = useState(0);
  const customerModalRef = useRef<CustomerModal>(null);

  const staffFilter = useRemoteOptions(fetchStaffOptions, !onlyMineRole);
  const date = periodStart.format("YYYY-MM-DD");
  const unit = period === "month" ? "month" : "week";

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchSummary = useCallback(async () => {
    const res = await saleStatsApi.summary({ staffId: onlyMineRole ? undefined : staffId, period, date });
    setSummary(res.data as SaleStatsSummary);
  }, [onlyMineRole, staffId, period, date]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await saleStatsApi.list({
        section,
        staffId: onlyMineRole ? undefined : staffId,
        period,
        date,
        page: paging.page,
        limit: paging.limit,
      });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [onlyMineRole, paging.limit, paging.page, period, section, staffId, date]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const resetPage = useCallback(() => setPaging((prev) => ({ ...prev, page: 1 })), []);

  const rowPermission = ROW_PERMISSION[section];
  const rowClickable = !!rowPermission && can(rowPermission, "VIEW");

  const handleRowClick = useCallback(
    async (row: SaleStatsRow) => {
      if (section === "customers") {
        const res = await customerApi.findOne(row.id);
        customerModalRef.current?.handleView(res.data as Customer);
      } else if (section === "orders") {
        setOrderId(row.id);
      } else if (section === "opportunities" || section === "quotes") {
        setOpportunityId(row.id);
      }
    },
    [section],
  );

  const options = useMemo(() => {
    const list = staffFilter.options;
    return staffId && !list.some((option) => option.value === staffId) ? [{ label: staffLabel, value: staffId }, ...list] : list;
  }, [staffFilter.options, staffId, staffLabel]);

  const isCurrentPeriod = periodStart.isSame(startOfPeriod(dayjs(), period), "day");
  const periodLabel =
    period === "month"
      ? `Tháng ${periodStart.format("MM/YYYY")}`
      : `Tuần ${periodStart.format("DD/MM")} – ${periodStart.add(6, "day").format("DD/MM/YYYY")}`;
  const { showingFrom, showingTo } = getTableShowingRange(paging.page, paging.limit, total);
  const counts = summary?.counts;

  return (
    <div className="saleStats flex flex-col gap-4">
      <div className="saleStats__bar">
        <Segmented
          value={period}
          options={[
            { label: "Tuần", value: "week" },
            { label: "Tháng", value: "month" },
          ]}
          onChange={(value) => {
            const next = value as SaleStatsPeriod;
            resetPage();
            setPeriod(next);
          }}
        />
        <div className="flex items-center gap-1">
          <AppButton
            tone="light"
            aria-label={period === "month" ? "Tháng trước" : "Tuần trước"}
            onClick={() => {
              resetPage();
              setAnchor((prev) => prev.subtract(1, unit));
            }}
          >
            <LeftOutlined />
          </AppButton>
          <div className="saleStats__weekLabel">{periodLabel}</div>
          <AppButton
            tone="light"
            aria-label={period === "month" ? "Tháng sau" : "Tuần sau"}
            onClick={() => {
              resetPage();
              setAnchor((prev) => prev.add(1, unit));
            }}
          >
            <RightOutlined />
          </AppButton>
        </div>
        <AppButton
          tone="light"
          disabled={isCurrentPeriod}
          onClick={() => {
            resetPage();
            setAnchor(dayjs());
          }}
        >
          {period === "month" ? "Tháng này" : "Tuần này"}
        </AppButton>
        {onlyMineRole ? (
          <span className="saleStats__staff">{summary?.staff.name || staffLabel}</span>
        ) : (
          <AppSelect
            showSearch
            filterOption={false}
            style={{ width: 260 }}
            placeholder="Chọn sale"
            options={options}
            value={staffId}
            onSearch={staffFilter.onSearch}
            onChange={(value, option) => {
              staffFilter.select(value);
              setStaffId(value);
              setStaffLabel(String((option as { label?: string })?.label || ""));
              resetPage();
            }}
          />
        )}
      </div>

      <div className="saleStats__cards">
        {SECTIONS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`saleStats__card ${section === item.key ? "isActive" : ""}`}
            onClick={() => {
              setSection(item.key);
              resetPage();
            }}
          >
            <span className="saleStats__cardLabel">{item.label}</span>
            <span className="saleStats__cardValue">{formatMoney(countOf(counts, item.key))}</span>
            {item.key === "debts" && <span className="saleStats__cardNote">{formatMoney(counts?.debtAmount || 0)}đ</span>}
          </button>
        ))}
      </div>

      <TableLayout
        table={
          <AppTable<SaleStatsRow>
            rowKey="id"
            loading={loading}
            pagination={false}
            dataSource={rows}
            columns={COLUMNS[section]}
            onRow={
              rowClickable
                ? (row) => ({ onClick: () => handleRowClick(row), style: { cursor: "pointer" } })
                : undefined
            }
          />
        }
        footer={
          <TableLayoutFooter
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={total}
            currentPage={paging.page}
            pageSize={paging.limit}
            onChange={setPaging}
          />
        }
      />

      <CustomerModal
        ref={customerModalRef}
        onSubmitOk={() => {
          fetchList();
          fetchSummary();
        }}
        onClose={() => {}}
      />
      <OrderDetailModal orderId={orderId} open={orderId > 0} onClose={() => setOrderId(0)} onChanged={fetchList} />
      <OpportunityDetailModal
        opportunityId={opportunityId}
        open={opportunityId > 0}
        onClose={() => setOpportunityId(0)}
      />
    </div>
  );
});
